import React, { useContext, useEffect, useRef, useState } from 'react';
import qs from 'qs';
import { Divider, Modal } from 'antd';
import 'antd/dist/antd.css';
import '@app/index.css';
import { includes } from 'lodash-es';
import { useRecoilValue } from 'recoil';
import { Font } from '@react-pdf/renderer';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import styled from 'styled-components';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import { AuthProvider, AuthContext } from '@app/providers/firebase/AuthProvider';
import { AuthStateSync } from '@ui/AuthStateSync';
import ContentWrapper from '@features/ContentWrapper';
import AppSections from './AppSections';
import MessageCenterDrawer from '@features/MessageCenter/pages/MessageCenterDrawer';
import SettingsDrawer from '@features/Settings/pages/SettingsDrawer';
import FirebaseStatus from '@app/providers/firebase/FirebaseStatus';
import DesktopMenubar from '@ui/Nav/Menubar';
import MobileNavbar from '@ui/Nav/MobileNavbar';
import EnvironmentHopButton from '@ui/Nav/EnvironmentHopButton';
import { UpdateNotification } from '@ui/UpdateNotification';
import { useServiceWorker } from '@app/hooks/useServiceWorker';

import theme from '@config/theme';
import { BODY_SIZE } from '@config/typography/constants';
import QBOAuth from '@services/api/QBO/auth';
import { useTestDataRemote } from '@lib/util/site';
import { QBO_AUTH_PATH, QBO_USERS_ATOM } from '@shared/state/qboState';


import ScopedComponent from '@ui/Utility/ScopedComponent';

import RedHatDisplayLight from '@assets/fonts/RedHatDisplay_Light.ttf';
import RedHatDisplayRegular from '@assets/fonts/RedHatDisplay_Regular.ttf';
import RedHatDisplayBold from '@assets/fonts/RedHatDisplay_Bold.ttf';
import RedHatDisplaySemiBold from '@assets/fonts/RedHatDisplay_Semibold.ttf';

// Register each weight as its own font family
Font.register({ src: RedHatDisplayRegular, family: 'RedHatDisplay' });
Font.register({ src: RedHatDisplayLight, family: 'RedHatDisplay-Light' });
Font.register({ src: RedHatDisplayBold, family: 'RedHatDisplay-Bold' });
Font.register({ src: RedHatDisplaySemiBold, family: 'RedHatDisplay-SemiBold' });



const NavDivider = styled(Divider)`
  display: none;
  @media ${theme.device.laptopL} {
    display: block;
    width: 100%;
    margin: 4px 0 4px 0;
    border-color: transparent;
  }
`;

const AppSectionWrapper = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;
  top: 48px; // mobile navbar height (0 padding)
  @media ${theme.device.laptopL} {
    top: 56px; // desktop fixed menubar height (56px)
  }
`;

const LocationTracker = () => {
  const location = useLocation();

  const titles: Record<string, string> = {
    schedule: 'Production Plan',
    router: 'Shop Router',
    pricing: 'Pricing',
    'pricing/edit': 'Edit Pricing',
    orders: 'Orders',
    'util/parsers': 'Utilties',
    customers: 'Customers',
    shipments: 'Shipments',
    scanner: 'Shop Scanner',
    inventory: 'Inventory',
    vendors: 'Vendors',
    'purchase-orders': 'Purchase Orders',
    'nc/report': 'Non-Compliance',
    'nc/scanner': 'Non-Compliance',
    'pricing/new-parts': 'New Parts',
  };

  const searchTitles = {
    'orders/create': 'Order Detail',
    'inventory/edit': 'Inventory Detail',
  };

  const getPageTitle = (pathname: string, search: string): string => {
    const root = pathname.replace('/', '');
    let title = 'Production Plan'; // Default title

    if (!search) {
      title = titles[root] || 'Production Plan';
    } else {
      const queryParams = qs.parse(search.replace('?', ''));
      if (root === 'orders/create') {
        const { customer, salesOrder } = queryParams;
        if (customer && salesOrder) {
          title = `${customer} Order #${salesOrder} - Detail`;
        } else {
          title = 'Order Detail';
        }
      } else if (root === 'inventory') {
        title = 'Inventory List';
      } else if (root === 'inventory/edit') {
        const { partId } = queryParams;
        if (partId) {
          title = `${partId} - Inventory Detail`;
        } else {
          title = 'Inventory Detail';
        }
      } else if (root === 'shipments/edit') {
        title = 'Shipment Detail';
      } else if (root === 'pricing/body' || root === 'pricing/neck') {
        const { partId } = queryParams;
        if (partId) {
          title = `${partId} - Pricing Detail`;
        } else {
          title = 'Customer Part Detail';
        }
      } else if (root === 'pd') {
        title = 'Product Development';
      } else {
        // Fallback for other search pages if needed, or keep default
        title = titles[root] || 'HELM';
      }
    }
    return title;
  };

  useEffect(() => {
    console.log('App level location change detected:', location.pathname + location.search);
  }, [location.pathname, location.search]);

  return (
    <>
     {/* <AppSections />  */}
    </>
  );
};

const App = () => {
  const [renderKey, setRenderKey] = useState(0);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  
  // Service worker for update notifications
  const { isUpdateAvailable, updateServiceWorker } = useServiceWorker();
  
  useEffect(() => {
    const handleForceRender = () => {
      console.log('Forcing app re-render on navigation');
      setRenderKey(prev => prev + 1);
    };
    
    // Listen for custom navigation events
    window.addEventListener('forceAppRender', handleForceRender);
    
    // Listen for browser back/forward button clicks
    const handlePopState = () => {
      console.log('Browser popstate detected, forcing re-render');
      setTimeout(() => handleForceRender(), 50);
    };
    window.addEventListener('popstate', handlePopState);
    
    // Intercept all history changes
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      console.log('History pushState detected, forcing re-render');
      setTimeout(() => handleForceRender(), 50);
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      console.log('History replaceState detected, forcing re-render');
      setTimeout(() => handleForceRender(), 50);
    };
    
    return () => {
      window.removeEventListener('forceAppRender', handleForceRender);
      window.removeEventListener('popstate', handlePopState);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);
  
  const qboAuthPath = useRecoilValue(QBO_AUTH_PATH);
  const { currentUser } = useContext(AuthContext);
  const qboUserEmails = useRecoilValue(QBO_USERS_ATOM);
  const qboInitRef = useRef(false);

  // Initial QBO auth check - only runs once
  useEffect(() => {
    if (!qboInitRef.current && currentUser && includes(qboUserEmails, currentUser.email)) {
      qboInitRef.current = true;
      QBOAuth.refreshToken(qboAuthPath)
        .catch((e) => {
          Modal.error({
            title: 'QBO Connection Error',
            content: 'Something went wrong with the connection to Quickbooks Online. Please let Keith know.',
          });
        });
    }
  }, [currentUser]);

  const GlobalStyle = createGlobalStyle`
    html {
      font-size: ${BODY_SIZE}px;
    }
    body {
      background-color: ${useTestDataRemote ? 'rgba(255, 0, 0, 0.075)' : window.location.href.match(/nc-scanner/) ? '#F0F0F0' : '#FFF'};
      & p {
        color: ${useTestDataRemote ? '#FF0000' : 'unset'};
      }
      & h3 {
        color: ${useTestDataRemote ? '#FF0000' : 'unset'};
      }
      & h6 {
        color: ${useTestDataRemote ? '#FF0000' : 'unset'};
      }
    }

    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: ${useTestDataRemote ? '#FF0000' : '#1E62D3'};
      border-color: ${useTestDataRemote ? '#FF0000' : '#1E62D3'};
    }
    .ant-btn-primary {
      background-color: ${useTestDataRemote ? '#7a211b' : '#1E62D3'};
      border-color: ${useTestDataRemote ? '#7a211b' : '#1E62D3'};
      
      &:hover {
        border-color: ${useTestDataRemote ? '#c72d22' : '#1E62D3'};
        background-color: ${useTestDataRemote ? '#c72d22' : '#1E62D3'};
        filter: brightness(95%);
      }
    }
    .ant-radio-group-solid .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
      background-color: ${useTestDataRemote ? '#FF0000' : '#1E62D3'};
      border-color: ${useTestDataRemote ? '#FF0000' : '#1E62D3'};

      &:hover {
        border-color: ${useTestDataRemote ? '#FF0000' : '#1E62D3'};
        background-color: ${useTestDataRemote ? '#FF0000' : '#1E62D3'};
        filter: brightness(95%);
      }
    }
    .ant-menu-submenu-title, .ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-submenu:hover, .ant-menu-horizontal:not(.ant-menu-dark) > .ant-menu-item:hover {
      & a {
        color: ${useTestDataRemote ? '#FF0000' : '#1E62D3'};
      }
      &:hover {
        color: ${useTestDataRemote ? '#FF0000' : '#1E62D3'};
        //border-bottom: 2px solid ${useTestDataRemote ? '#FF0000' : '#1E62D3'};
      }
    }
    
    /* Ensure messages appear above all other elements */
    .ant-message {
      z-index: 99999999 !important;
    }
    
    .ant-message-notice {
      z-index: 99999999 !important;
    }
    
    .ant-message-notice-content {
      z-index: 99999999 !important;
    }
  `;

  return (
    <AuthProvider>
      <AuthStateSync />
              <ThemeProvider theme={theme} key={renderKey}>
        <GlobalStyle />
        <BrowserRouter>
          <LocationTracker />
          <MessageCenterDrawer />
          <SettingsDrawer />
          <FirebaseStatus />
          <EnvironmentHopButton />
          <ContentWrapper>
            <>
              <DesktopMenubar />
              <MobileNavbar />
            </>            
            <NavDivider />
            <AppSectionWrapper>
              <AppSections />
            </AppSectionWrapper>
          </ContentWrapper>
        </BrowserRouter>
        <UpdateNotification
          isVisible={isUpdateAvailable && !updateDismissed}
          onUpdate={updateServiceWorker}
          onDismiss={() => setUpdateDismissed(true)}
        />
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
